/**
 * KNK-1.55 / FR-11a — Settings → Add-on modules, for the ACTIVE account.
 *
 * Superadmin only: it renders nothing for anyone else, and the database refuses every read and
 * write here to anyone else anyway (`is_superadmin()` in account_modules_for, set_account_module,
 * account_module_usage). Rules and copy live in `lib/accountModules.ts`; this file renders.
 *
 * Replaces KNK-1.11's "Knock Mode for this account" switch, which lived in the Knock Mode panel.
 *
 * RBAC-6.1 / FR-18: also THE superadmin surface for per-account features. The five that were
 * tenant_settings columns (Contracts, Solar Design, Commission & job costing, PM-managed site
 * surveys, lead routing) are catalogue rows now, and any feature added later is one more row that
 * appears here with no code change.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { usePermissions } from '@/contexts/PermissionContext';
import { useTenant } from '@/contexts/TenantContext';
import { notifyKnockModeChanged } from '@/hooks/useKnockMode';
import {
  ACCOUNT_MODULES_COPY as COPY,
  BUILT_MODULES,
  draftError,
  formatMonth,
  moduleAccessNote,
  moduleStateLine,
  pacificToday,
  type AccountModule,
  type ModuleDraft,
  type ModuleState,
  type ModuleUsageMonth,
} from '@/lib/accountModules';
import { fetchAccountModules, fetchAccountModuleUsage, setAccountModule } from '@/services/accountModulesService';
import { GATE_MODULE_KEYS } from '@/lib/accountFeatures';
import { notifyAccountFeaturesChanged } from '@/lib/accountFeaturesCache';
import { clearCommissionCostingEnabledCache } from '@/lib/commissionCostingEnabledCache';

export function AccountModulesSettings() {
  const { hasPermission, loading: permsLoading } = usePermissions();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id ?? null;
  const isSuperadmin = hasPermission('system.override');

  const [modules, setModules] = useState<AccountModule[]>([]);
  const [usage, setUsage] = useState<ModuleUsageMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId || !isSuperadmin) return;
    setLoading(true);
    setError(null);
    try {
      const [m, u] = await Promise.all([fetchAccountModules(tenantId), fetchAccountModuleUsage(tenantId, 6)]);
      setModules(m);
      setUsage(u);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId, isSuperadmin]);

  useEffect(() => {
    void load();
  }, [load]);

  if (permsLoading || !isSuperadmin) return null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {COPY.title}
            {currentTenant?.company_name ? <span className="font-normal text-muted-foreground"> · {currentTenant.company_name}</span> : null}
          </CardTitle>
          <CardDescription>{COPY.intro}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>Could not read this account&apos;s modules: {error}</AlertDescription>
            </Alert>
          )}
          {loading && !error ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {modules.map((m) => (
                <ModuleRow key={`${tenantId}-${m.key}`} tenantId={tenantId!} module={m} onSaved={load} />
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">{COPY.trialRule}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{COPY.usageTitle}</CardTitle>
          <CardDescription>{COPY.usageIntro}</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {usage.length === 0 ? (
            <p className="text-sm text-muted-foreground">{COPY.usageEmpty}</p>
          ) : (
            <Table data-testid="account-module-usage">
              <TableHeader>
                <TableRow>
                  <TableHead>{COPY.usageCols.month}</TableHead>
                  <TableHead className="text-right">{COPY.usageCols.seeded}</TableHead>
                  <TableHead className="text-right">{COPY.usageCols.reps}</TableHead>
                  <TableHead className="text-right">{COPY.usageCols.lookups}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usage.map((u) => (
                  <TableRow key={u.month}>
                    <TableCell>{formatMonth(u.month)}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.seededDoors.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.activeKnockingReps.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.paidDataLookups.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ModuleRow({ tenantId, module: m, onSaved }: { tenantId: string; module: AccountModule; onSaved: () => Promise<void> }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<ModuleDraft>({ state: m.state, trialEndsOn: m.trialEndsOn ?? '' });
  const [busy, setBusy] = useState(false);

  const dirty = draft.state !== m.state || (draft.state === 'trial' && draft.trialEndsOn !== (m.trialEndsOn ?? ''));
  const today = pacificToday();
  const invalid = draftError(draft, today);

  const save = async () => {
    setBusy(true);
    try {
      await setAccountModule(tenantId, m.key, draft.state, draft.state === 'trial' ? draft.trialEndsOn : null);
      if (m.key === 'knock_mode') notifyKnockModeChanged();
      // RBAC-6.1: a feature gate's answer is cached per account for the page's life. Drop it, so this
      // superadmin's own screens follow the switch without a reload.
      if ((GATE_MODULE_KEYS as readonly string[]).includes(m.key)) notifyAccountFeaturesChanged();
      if (m.key === 'commission_costing') clearCommissionCostingEnabledCache();
      toast({ title: COPY.saved, description: `${m.name}: ${COPY.labels[draft.state]}` });
      await onSaved();
    } catch (e) {
      toast({ title: 'Not changed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex flex-col gap-3 p-4 lg:flex-row lg:items-start lg:justify-between" data-testid={`account-module-${m.key}`}>
      <div className="min-w-0 space-y-1">
        <p className="flex flex-wrap items-center gap-2 font-medium text-foreground">
          {m.name}
          <Badge variant={m.active ? 'default' : 'secondary'}>{moduleStateLine(m)}</Badge>
        </p>
        <p className="text-sm text-muted-foreground">{m.description}</p>
        {moduleAccessNote(m.key) && <p className="text-xs text-muted-foreground">{moduleAccessNote(m.key)}</p>}
        {!BUILT_MODULES.has(m.key) && <p className="text-xs text-muted-foreground">{COPY.notBuiltYet}</p>}
        {m.updatedAt && (
          <p className="text-xs text-muted-foreground">{COPY.changedBy(m.updatedByName, new Date(m.updatedAt).toLocaleString())}</p>
        )}
        {dirty && invalid && <p className="text-xs text-destructive">{invalid}</p>}
      </div>
      <div className="flex flex-wrap items-end gap-2 lg:shrink-0">
        <Select value={draft.state} onValueChange={(v) => setDraft((d) => ({ ...d, state: v as ModuleState }))}>
          <SelectTrigger className="w-28" aria-label={`${m.name} state`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(['off', 'on', 'trial'] as const).map((s) => (
              <SelectItem key={s} value={s}>
                {COPY.labels[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {draft.state === 'trial' && (
          <label className="flex flex-col text-xs text-muted-foreground">
            {COPY.lastDay}
            <Input
              type="date"
              className="w-40"
              min={today}
              value={draft.trialEndsOn}
              onChange={(e) => setDraft((d) => ({ ...d, trialEndsOn: e.target.value }))}
            />
          </label>
        )}
        <Button onClick={save} disabled={!dirty || invalid !== null || busy}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {COPY.save}
        </Button>
      </div>
    </li>
  );
}
